import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, Output, Input, ViewEncapsulation, Component, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@engineering11/ui-lib/e11-tooltip';
import { E11TooltipModule } from '@engineering11/ui-lib/e11-tooltip';

const fontSizeMap = {
    sm: 'e11-text-sm',
    md: 'e11-text-md',
    lg: 'e11-text-[18px] e11-font-bold',
    xl: 'e11-text-[20px] e11-font-bold',
};
const defaultRequiredTooltip = { tooltipText: 'This is a required field', icon: 'publish' };
class E11InlineLabelComponent {
    constructor() {
        this.size = 'md';
        this.loading = false;
        this.hasError = false;
        this.required = false;
        this.editIcon = false;
        this.labelClicked = new EventEmitter();
        this.fontSizeMap = fontSizeMap;
        this.defaultRequiredTooltip = defaultRequiredTooltip;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11InlineLabelComponent, selector: "e11-inline-label", inputs: { label: "label", value: "value", placeholder: "placeholder", size: "size", loading: "loading", hasError: "hasError", required: "required", editIcon: "editIcon", tooltipConfig: "tooltipConfig", requiredTooltipConfig: "requiredTooltipConfig" }, outputs: { labelClicked: "labelClicked" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11InlineLabelComponent),
                multi: true,
            },
        ], ngImport: i0, template: `
    <div *ngIf="loading" class="e11-flex e11-skeleton e11-h-7"></div>
    <div
      *ngIf="!loading"
      (click)="labelClicked.emit()"
      class="e11-flex e11-items-center e11-h-7 e11-p-1 hover:e11-bg-skin-grey/20 e11-cursor-pointer"
    >
      <div class="e11-line-clamp-1 {{ fontSizeMap[size] }}">
        <span class="e11-mr-1" [ngClass]="{ 'e11-font-skin-secondary': fontSizeMap[size] === fontSizeMap.lg }">{{
          this.label ?? this.value ?? this.placeholder
        }}</span>
        <span *ngIf="label" class="e11-mr-2" [ngClass]="{ 'e11-italic e11-text-skin-light': !value }">{{ this.value ?? this.placeholder }}</span>
      </div>
      <e11-tooltip
        *ngIf="required"
        [color]="'dark'"
        [tooltipText]="requiredTooltipConfig ? requiredTooltipConfig.tooltipText : defaultRequiredTooltip.tooltipText"
      >
        <span class="material-icons e11-mr-1" [ngClass]="hasError ? 'e11-text-skin-error' : 'e11-text-skin-success'">{{
          requiredTooltipConfig ? requiredTooltipConfig.icon : defaultRequiredTooltip.icon
        }}</span>
      </e11-tooltip>
      <div *ngIf="editIcon" class="e11-flex e11-justify-center e11-items-center">
        <span class="material-icons-outlined md-18 e11-mr-1 e11-text-skin-primary-accent">edit</span>
      </div>
      <div *ngIf="tooltipConfig">
        <e11-tooltip [color]="'dark'" [tooltipText]="tooltipConfig.tooltipText">
          <span class="material-icons-outlined md-18">{{ tooltipConfig.icon }}</span>
        </e11-tooltip>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.E11TooltipComponent, selector: "e11-tooltip", inputs: ["tooltipText", "tooltipClassOverrides", "hideTooltip", "color", "tooltipContent", "position", "interactive", "maxWidth", "delay"], outputs: ["tooltipClick"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineLabelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-inline-label',
                    template: `
    <div *ngIf="loading" class="e11-flex e11-skeleton e11-h-7"></div>
    <div
      *ngIf="!loading"
      (click)="labelClicked.emit()"
      class="e11-flex e11-items-center e11-h-7 e11-p-1 hover:e11-bg-skin-grey/20 e11-cursor-pointer"
    >
      <div class="e11-line-clamp-1 {{ fontSizeMap[size] }}">
        <span class="e11-mr-1" [ngClass]="{ 'e11-font-skin-secondary': fontSizeMap[size] === fontSizeMap.lg }">{{
          this.label ?? this.value ?? this.placeholder
        }}</span>
        <span *ngIf="label" class="e11-mr-2" [ngClass]="{ 'e11-italic e11-text-skin-light': !value }">{{ this.value ?? this.placeholder }}</span>
      </div>
      <e11-tooltip
        *ngIf="required"
        [color]="'dark'"
        [tooltipText]="requiredTooltipConfig ? requiredTooltipConfig.tooltipText : defaultRequiredTooltip.tooltipText"
      >
        <span class="material-icons e11-mr-1" [ngClass]="hasError ? 'e11-text-skin-error' : 'e11-text-skin-success'">{{
          requiredTooltipConfig ? requiredTooltipConfig.icon : defaultRequiredTooltip.icon
        }}</span>
      </e11-tooltip>
      <div *ngIf="editIcon" class="e11-flex e11-justify-center e11-items-center">
        <span class="material-icons-outlined md-18 e11-mr-1 e11-text-skin-primary-accent">edit</span>
      </div>
      <div *ngIf="tooltipConfig">
        <e11-tooltip [color]="'dark'" [tooltipText]="tooltipConfig.tooltipText">
          <span class="material-icons-outlined md-18">{{ tooltipConfig.icon }}</span>
        </e11-tooltip>
      </div>
    </div>
  `,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11InlineLabelComponent),
                            multi: true,
                        },
                    ],
                    encapsulation: ViewEncapsulation.None,
                }]
        }], propDecorators: { label: [{
                type: Input
            }], value: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], size: [{
                type: Input
            }], loading: [{
                type: Input
            }], hasError: [{
                type: Input
            }], required: [{
                type: Input
            }], editIcon: [{
                type: Input
            }], tooltipConfig: [{
                type: Input
            }], requiredTooltipConfig: [{
                type: Input
            }], labelClicked: [{
                type: Output
            }] } });

class E11InlineLabelModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineLabelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11InlineLabelModule, declarations: [E11InlineLabelComponent], imports: [CommonModule, ReactiveFormsModule, E11TooltipModule], exports: [E11InlineLabelComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineLabelModule, imports: [CommonModule, ReactiveFormsModule, E11TooltipModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineLabelModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11InlineLabelComponent],
                    imports: [CommonModule, ReactiveFormsModule, E11TooltipModule],
                    exports: [E11InlineLabelComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11InlineLabelComponent, E11InlineLabelModule, fontSizeMap };
